import { c } from "react/compiler-runtime";
import { Activity, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { PresenceChild, invariant } from "motion/react";
/**
* Custom floating-ui middleware which calculates transform-origin X + Y offsets
* based on the current floating rect's dimensions and shift offset.
*
* Scaling popovers with these transform-origin offsets will give the effect of
* popvers slightly 'growing' from the origin/reference element.
*
* This middleware must be applied after both `@sanity/ui/size` and `shift` middlewares.
*/
const origin = {
	name: "@sanity/ui/origin",
	fn({ middlewareData, placement, rects }) {
		let [side] = placement.split("-"), floatingWidth = rects.floating.width, floatingHeight = rects.floating.height, shiftX = middlewareData.shift?.x || 0, shiftY = middlewareData.shift?.y || 0;
		if (floatingWidth <= 0 || floatingHeight <= 0) return {};
		let { originX, originY } = ["bottom", "top"].includes(side) ? {
			originX: clamp(.5 - shiftX / floatingWidth, 0, 1),
			originY: side === "bottom" ? 0 : 1
		} : {
			originX: +(side === "left"),
			originY: clamp(.5 - shiftY / floatingHeight, 0, 1)
		};
		return { data: {
			originX,
			originY
		} };
	}
};
function clamp(num, min, max) {
	return Math.min(Math.max(num, min), max);
}
/**
* MIT License
*
* Copyright (c) 2024 Motion One
*
* Permission is hereby granted, free of charge, to any person obtaining a copy
* of this software and associated documentation files (the "Software"), to deal
* in the Software without restriction, including without limitation the rights
* to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
* copies of the Software, and to permit persons to whom the Software is
* furnished to do so, subject to the following conditions:
*
* The above copyright notice and this permission notice shall be included in all
* copies or substantial portions of the Software.
*
* THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
* IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
* FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
* AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
* LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
* OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
* SOFTWARE.
*/
/**
* Animated version of React's `Activity` that defers `mode="hidden"` until
* child exit animations have finished.
*
* @internal
*/
function AnimateActivity(t0) {
	let $ = c(9), { mode: modeFromProps, layoutMode, children } = t0, [mode, setMode] = useState(modeFromProps), isPresent = modeFromProps === "visible";
	if (isPresent && mode !== "visible") return setMode("visible"), null;
	let t1;
	$[0] === Symbol.for("react.memo_cache_sentinel") ? (t1 = () => setMode("hidden"), $[0] = t1) : t1 = $[0];
	let onExitComplete = t1;
	invariant(!!Activity, "Activity component not found - upgrade to React 19.2.0 or higher");
	let t2 = isPresent ? void 0 : onExitComplete, t3 = layoutMode === "pop" ? "popLayout" : "sync", t4 = children, t5;
	$[1] !== isPresent || $[2] !== t2 || $[3] !== t3 || $[4] !== t4 ? (t5 = /* @__PURE__ */ jsx(PresenceChild, {
		isPresent,
		onExitComplete: t2,
		presenceAffectsLayout: !1,
		mode: t3,
		children: t4
	}), $[1] = isPresent, $[2] = t2, $[3] = t3, $[4] = t4, $[5] = t5) : t5 = $[5];
	let t6;
	return $[6] !== mode || $[7] !== t5 ? (t6 = /* @__PURE__ */ jsx(Activity, {
		mode,
		children: t5
	}), $[6] = mode, $[7] = t5, $[8] = t6) : t6 = $[8], t6;
}
function getElementRef(element) {
	let getter = Object.getOwnPropertyDescriptor(element.props, "ref")?.get, mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
	return mayWarn ? element.ref : (getter = Object.getOwnPropertyDescriptor(element, "ref")?.get, mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning, mayWarn ? element.props.ref : element.props.ref || element.ref);
}
export { AnimateActivity as n, origin as r, getElementRef as t };

//# sourceMappingURL=getElementRef-5HZQ-Owu.js.map